import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * A wrapper around React.lazy that handles chunk load errors by reloading the page.
 * This is useful when a new deployment occurs and old chunks are no longer available.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyImport = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): LazyExoticComponent<T> => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      // Check for common chunk load error messages
      const isChunkError = 
        errorMessage.includes('Failed to fetch dynamically imported module') ||
        errorMessage.includes('Importing a module script failed') ||
        errorMessage.includes('error loading dynamically imported module');

      if (isChunkError) {
        // Prevent infinite reload loops using session storage
        const storageKey = `retry-${window.location.href}`;
        const retried = sessionStorage.getItem(storageKey);
        
        if (!retried) {
          console.log('Chunk load error detected, reloading page...');
          sessionStorage.setItem(storageKey, 'true');
          window.location.reload();
          // Return a never-resolving promise to hold the suspense state while reloading
          return new Promise(() => {});
        } else {
          // If we already retried and it failed again, clean up and throw
          sessionStorage.removeItem(storageKey);
          console.error('Chunk load error persisted after reload');
        }
      }
      throw error;
    }
  });
};
